import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

export interface OrderSummary {
  items: OrderItem[];
  total: number;
  currency: string;
}

export interface OrderItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

export interface ContactInformation {
  bestellnummer: string;
  name: string;
  email: string;
  notiz: string;
  abholstation: string;
  datum: string;
}

@Component({
  selector: 'app-order-summary',
  templateUrl: './order-summary.component.html',
  styleUrls: ['./order-summary.component.scss']
})
export class OrderSummaryComponent implements OnInit {
  @Output() orderCompleted = new EventEmitter<{order: OrderSummary, contact: ContactInformation}>();

  contactForm: FormGroup;
  isEditingOrder = false;
  isEditingContact = false;

  // Sample order data matching the screenshot
  orderSummary: OrderSummary = {
    items: [
      {
        id: 'placeholder-1',
        name: 'Platzhalter',
        price: 13.50,
        quantity: 1
      },
      {
        id: 'placeholder-2',
        name: 'Platzhalter', 
        price: 13.50,
        quantity: 1
      }
    ],
    total: 27.00,
    currency: 'CHF'
  };

  // Default contact information matching screenshot
  defaultContact: ContactInformation = {
    bestellnummer: '345',
    name: 'Phil Revdev',
    email: 'mail@randommail.com',
    notiz: 'Some Comment',
    abholstation: 'Self-service restaurant',
    datum: 'Montag, 04.8.2024'
  };

  constructor(
    private fb: FormBuilder,
    private router: Router
  ) {
    this.createContactForm();
  }

  ngOnInit() {
    this.loadContactInformation();
  }

  private createContactForm() {
    this.contactForm = this.fb.group({
      bestellnummer: ['', Validators.required],
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      notiz: [''],
      abholstation: ['', Validators.required],
      datum: ['', Validators.required]
    });
  }

  private loadContactInformation() {
    // Load contact information (from localStorage, service, etc.)
    this.contactForm.patchValue(this.defaultContact);
  }

  // Order editing
  onEditOrder() {
    this.isEditingOrder = true;
    // Navigate back to cart or implement inline editing
    this.router.navigate(['/cart']);
  }

  // Contact editing
  onEditContact() {
    this.isEditingContact = !this.isEditingContact;
  }

  // Save contact information
  onSaveContact() {
    if (this.contactForm.valid) {
      this.isEditingContact = false;
      // Save contact information
    }
  }

  // Cancel contact editing
  onCancelContactEdit() {
    this.isEditingContact = false;
    this.loadContactInformation(); // Reload original data
  }

  // Complete order
  onContinue() {
    if (this.contactForm.valid) {
      const orderData = {
        order: this.orderSummary,
        contact: this.contactForm.value
      };
      
      this.orderCompleted.emit(orderData);
      // Navigate to confirmation or payment
      this.router.navigate(['/payment']);
    } else {
      // Show validation errors
      this.markFormGroupTouched();
    }
  }

  private markFormGroupTouched() {
    Object.keys(this.contactForm.controls).forEach(key => {
      const control = this.contactForm.get(key);
      control?.markAsTouched();
    });
  }

  // Helper methods
  formatPrice(price: number, currency: string = 'CHF'): string {
    return `${price.toFixed(2)} ${currency}`;
  }

  getTotalPrice(): number {
    return this.orderSummary.items.reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0);
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.contactForm.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  getFieldError(fieldName: string): string {
    const field = this.contactForm.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) {
        return `${fieldName} ist erforderlich`;
      }
      if (field.errors['email']) {
        return 'Ungültige E-Mail-Adresse';
      }
    }
    return '';
  }
}